import { useState } from 'react';
import { EmptyState } from '../components/States';
import { useTranslation } from 'react-i18next';
import type { Shipment } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { variantLabel } from '../components/Operation';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

interface LineInput {
  received: string;
  missing: string;
  damaged: string;
  surplus: string;
}

/**
 * Recepción en el local: por cada línea, recibido + faltante + averiado = despachado + sobrante (R4).
 * El sobrante entra dentro de lo recibido y queda pendiente de que Gerencia lo regularice (Etapa 2).
 */
export function ReceiveStore() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const mine = new Set((user?.locations ?? []).filter((l) => l.type === 'STORE').map((l) => l.id));
  const pending = useLoad(() => api.shipments({ status: 'OPEN' }), []);
  const rows = (pending.data ?? []).filter((s) => !user?.role.scoped || mine.has(s.destination_id));
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [inputs, setInputs] = useState<Record<number, LineInput>>({});
  const [note, setNote] = useState('');

  const open = (s: Shipment) => {
    setShipment(s);
    setNote('');
    setInputs(Object.fromEntries(s.lines.map((l) => [l.line_id, { received: String(l.qty), missing: '0', damaged: '0', surplus: '0' }])));
  };
  const n = (v: string | undefined) => Math.max(0, Math.floor(Number(v) || 0));
  const lineOk = (l: { line_id: number; qty: number }) => {
    const i = inputs[l.line_id];
    return n(i?.received) + n(i?.missing) + n(i?.damaged) === l.qty + n(i?.surplus) && n(i?.surplus) <= n(i?.received);
  };
  const balanced = !!shipment && shipment.lines.every(lineOk);
  const hasSurplus = !!shipment && shipment.lines.some((l) => n(inputs[l.line_id]?.surplus) > 0);

  const confirm = () =>
    run(async () => {
      if (!shipment) return;
      const scope = `recepcion:${shipment.id}`;
      try {
        const r = await api.receiveShipment(requestKey(scope), shipment.id, {
          lines: shipment.lines.map((l) => ({ line_id: l.line_id, received: n(inputs[l.line_id]?.received), missing: n(inputs[l.line_id]?.missing), damaged: n(inputs[l.line_id]?.damaged), surplus: n(inputs[l.line_id]?.surplus) })),
          note,
        });
        releaseKey(scope);
        setShipment(null);
        show(t(r.shipment_status === 'RECEIVED_WITH_DIFFERENCE' ? 'receive.doneWithDifference' : 'receive.done', { doc: shipment.doc_no }));
        void pending.reload();
      } catch (e) {
        showError(e);
      }
    });

  return (
    <Panel title={t('receive.title')} description={t('receive.description')}>
      <div id="storePending" className="alerts">
        {rows.map((s) => (
          <div className="alert" key={s.id} data-shipment={s.doc_no}>
            <div>
              <b>{[s.doc_no, t('ops.unitsCount', { n: formatNumber(s.units, lang) }), s.destination_name].join(' · ')}</b>
              <small>{t('receive.sent', { value: formatMoney(s.total ?? 0, lang), date: formatDate(s.dispatched_at, lang), origin: s.origin_name })}</small>
            </div>
            <button type="button" className="btn green" data-receive={s.doc_no} onClick={() => open(s)}>
              {t('receive.button')}
            </button>
          </div>
        ))}
        {pending.data && rows.length === 0 ? <EmptyState icon="receiveStore" title={t('receive.empty')} hint={t('receive.emptyHint')} /> : null}
      </div>

      <Modal open={!!shipment} title={t('receive.modalTitle', { doc: shipment?.doc_no ?? '' })} help={t('receive.modalHelp')} onClose={() => setShipment(null)}>
        {shipment ? (
          <div id="receiveForm">
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('receive.product')}</th>
                    <th>{t('receive.dispatched')}</th>
                    <th>{t('receive.received')}</th>
                    <th>{t('receive.missing')}</th>
                    <th>{t('receive.damaged')}</th>
                    <th>{t('receive.surplus')}</th>
                  </tr>
                </thead>
                <tbody id="receiveLines">
                  {shipment.lines.map((l) => {
                    const i = inputs[l.line_id];
                    const ok = lineOk(l);
                    const set = (p: Partial<LineInput>) => setInputs({ ...inputs, [l.line_id]: { ...i!, ...p } });
                    return (
                      <tr key={l.line_id} data-line={l.line_id} className={ok ? '' : 'rowbad'}>
                        <td>
                          <b>{l.ref}</b> {l.product_name}
                          <br />
                          <span className="tiny muted">{variantLabel(l)}</span>
                        </td>
                        <td>{l.qty}</td>
                        <td><input className="input" style={{ width: 80 }} type="number" min={0} data-field="received" value={i?.received ?? ''} onChange={(e) => set({ received: e.target.value })} /></td>
                        <td><input className="input" style={{ width: 80 }} type="number" min={0} data-field="missing" value={i?.missing ?? ''} onChange={(e) => set({ missing: e.target.value })} /></td>
                        <td><input className="input" style={{ width: 80 }} type="number" min={0} data-field="damaged" value={i?.damaged ?? ''} onChange={(e) => set({ damaged: e.target.value })} /></td>
                        <td><input className="input" style={{ width: 80 }} type="number" min={0} data-field="surplus" value={i?.surplus ?? ''} onChange={(e) => set({ surplus: e.target.value })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={balanced ? 'paystatus' : 'paystatus bad'} id="receiveStatus" style={{ marginTop: 10 }}>
              {t(balanced ? 'receive.balanced' : 'receive.unbalanced')}
              {balanced && hasSurplus ? ` ${t('receive.surplusPending')}` : ''}
            </div>
            <div className="field" style={{ marginTop: 10 }}>
              <label htmlFor="receiveNote">{t('receive.note')}</label>
              <input id="receiveNote" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <button type="button" className="btn green" id="receiveConfirm" style={{ marginTop: 10, width: '100%' }} disabled={!balanced || busy} onClick={() => void confirm()}>
              {t('receive.confirm')}
            </button>
          </div>
        ) : null}
      </Modal>
    </Panel>
  );
}
